import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Button,
  IconButton,
  Box,
  Typography,
  Snackbar,
  Alert,
  MenuItem,
  Grid,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import { tokens } from "../../theme";
import Carousel from "react-bootstrap/Carousel";
import "bootstrap/dist/css/bootstrap.min.css";
import productService from "../../repository/productRepository";

const EditProductDialog = ({ open, onClose, product, onSave }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [formData, setFormData] = useState({
    productName: "",
    product_ShortDescription: "",
    actual_price: "",
    max_price: "",
    s_Price: "",
    stockCount: "",
    productStatus: true,
    product_Description: "",
    mainCategoryId: "",
    categoryId: "",
    subCategoryId: "",
    imageUrls: [],
  });
  const [selectedImages, setSelectedImages] = useState([]);

  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  useEffect(() => {
    if (product) {
      setFormData({
        productName: product.productName || "",
        product_ShortDescription: product.product_ShortDescription || "",
        actual_price: product.actual_price || "",
        max_price: product.max_price || "",
        s_Price: product.s_Price || "",
        stockCount: product.stockCount || "",
        productStatus: product.productStatus || true,
        product_Description: product.product_Description || "",
        mainCategoryId: product.mainCategoryId || "",
        categoryId: product.categoryId || "",
        subCategoryId: product.subCategoryId || "",
        imageUrls: product.productImage || [],
      });
      setSelectedImages([]);
    }
  }, [product]);

  useEffect(() => {
    if (!open) {
      setFormData({
        productName: "",
        product_ShortDescription: "",
        actual_price: "",
        max_price: "",
        s_Price: "",
        stockCount: "",
        productStatus: true,
        product_Description: "",
        mainCategoryId: "",
        categoryId: "",
        subCategoryId: "",
        imageUrls: [],
      });
      setSelectedImages([]);
    }
  }, [open]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length + selectedImages.length > 8) {
      setSnackbarMessage("You can only upload a maximum of 8 images.");
      setSnackbarSeverity("warning");
      setOpenSnackbar(true);
      return;
    }
    const newImageUrls = files.map((file) => URL.createObjectURL(file));
    setSelectedImages((prev) => [...prev, ...newImageUrls]);
    setFormData((prev) => ({
      ...prev,
      imageUrls: [...prev.imageUrls],
    }));
  };

  const handleRemoveImage = (index) => {
    setSelectedImages((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    if (Number(formData.s_Price) < Number(formData.actual_price)) {
      setSnackbarMessage("Minimum price cannot be less than the actual price.");
      setSnackbarSeverity("warning");
      setOpenSnackbar(true);
      return;
    }

    try {
      const formDataToSend = new FormData();
      formDataToSend.append("productId", product.productId);
      Object.keys(formData).forEach((key) => {
        if (formData[key] !== undefined && formData[key] !== "") {
          if (key === "imageUrls") {
            formData.imageUrls.forEach((file) =>
              formDataToSend.append("imageURL", file),
            );
            selectedImages.forEach((file) =>
              formDataToSend.append("imageURL", file),
            );
          } else {
            formDataToSend.append(key, formData[key]);
          }
        }
      });

      // await axios.put('https://crmbackend.yadavtechnicalcorp.com/admin/editProduct', formDataToSend, {
      //     headers: {
      //         'Content-Type': 'multipart/form-data',
      //     },
      // });

      await productService.editProduct(formDataToSend);

      setSnackbarMessage("Product updated successfully!");
      setSnackbarSeverity("success");
      onSave();
    } catch (error) {
      setSnackbarMessage("Failed to update product");
      setSnackbarSeverity("error");
      console.error("Failed to update product", error);
    } finally {
      setOpenSnackbar(true);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={() => onClose(false)}
        fullWidth
        maxWidth="lg"
        PaperProps={{
          style: {
            backgroundColor:
              colors.background || theme.palette.background.default,
            color: colors.text || theme.palette.text.primary,
          },
        }}
      >
        <DialogTitle
          sx={{
            backgroundColor: colors.primary || theme.palette.primary.main,
            color:
              theme.palette.mode === "light"
                ? "black"
                : colors.light || theme.palette.grey[100],
            position: "relative",
          }}
        >
          Edit Product
          <IconButton
            aria-label="close"
            onClick={() => onClose(false)}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: colors.grey || theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                name="productName"
                label="Product Name"
                value={formData.productName}
                onChange={handleChange}
                fullWidth
                variant="outlined"
                sx={{ marginBottom: 2 }}
              />
              <TextField
                name="product_ShortDescription"
                label="Short Description"
                value={formData.product_ShortDescription}
                onChange={handleChange}
                fullWidth
                variant="outlined"
                sx={{ marginBottom: 2 }}
              />
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    name="s_Price"
                    label="Min Price"
                    type="number"
                    value={formData.s_Price}
                    onChange={handleChange}
                    fullWidth
                    variant="outlined"
                    sx={{ marginBottom: 2 }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    name="max_price"
                    label="Max Price"
                    type="number"
                    value={formData.max_price}
                    onChange={handleChange}
                    fullWidth
                    variant="outlined"
                    sx={{ marginBottom: 2 }}
                  />
                </Grid>
              </Grid>
              <TextField
                name="actual_price"
                label="Actual Price"
                type="number"
                value={formData.actual_price}
                onChange={handleChange}
                fullWidth
                variant="outlined"
                sx={{ marginBottom: 2 }}
              />
              <TextField
                name="stockCount"
                label="Stock Count"
                type="number"
                value={formData.stockCount}
                onChange={handleChange}
                fullWidth
                variant="outlined"
                sx={{ marginBottom: 2 }}
              />
              <TextField
                name="productStatus"
                label="Status"
                select
                value={formData.productStatus}
                onChange={handleChange}
                fullWidth
                variant="outlined"
                sx={{ marginBottom: 2 }}
              >
                <MenuItem value={true}>Active</MenuItem>
                <MenuItem value={false}>Inactive</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                name="product_Description"
                label="Product Description"
                value={formData.product_Description}
                onChange={handleChange}
                fullWidth
                variant="outlined"
                multiline
                rows={4}
                sx={{ marginBottom: 2 }}
              />
              <Grid container spacing={2} sx={{ marginTop: 2 }}>
                <Grid item xs={6}>
                  <Typography variant="h6" gutterBottom>
                    Images
                  </Typography>
                  <Carousel controls={false}>
                    {formData.imageUrls.length > 0 ? (
                      formData.imageUrls.map((img, index) => (
                        <Carousel.Item key={index}>
                          <Box sx={{ height: "200px" }}>
                            <img
                              className="d-block"
                              src={img}
                              alt={`Fetched Image ${index}`}
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "contain",
                              }}
                            />
                          </Box>
                        </Carousel.Item>
                      ))
                    ) : (
                      <Typography>No fetched images</Typography>
                    )}
                  </Carousel>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="h6" gutterBottom>
                    Uploaded Images
                  </Typography>
                  {selectedImages.length > 0 ? (
                    <Carousel controls={false}>
                      {selectedImages.map((img, index) => (
                        <Carousel.Item key={index}>
                          <Box sx={{ position: "relative", height: "200px" }}>
                            <img
                              src={img}
                              alt={`Uploaded Image ${index}`}
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "contain",
                              }}
                            />
                            <IconButton
                              onClick={() => handleRemoveImage(index)}
                              sx={{
                                position: "absolute",
                                top: 8,
                                right: 8,
                                color: "white",
                                backgroundColor: "rgba(0, 0, 0, 0.5)",
                                "&:hover": {
                                  backgroundColor: "rgba(0, 0, 0, 0.7)",
                                },
                              }}
                            >
                              <CloseIcon />
                            </IconButton>
                          </Box>
                        </Carousel.Item>
                      ))}
                    </Carousel>
                  ) : (
                    <Typography>No uploaded images</Typography>
                  )}
                  <Button
                    variant="contained"
                    component="label"
                    sx={{ marginTop: 2 }}
                  >
                    Upload Images
                    <input
                      type="file"
                      multiple
                      hidden
                      onChange={handleImageChange}
                      accept="image/*"
                    />
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={onClose} color="secondary">
            Cancel
          </Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={handleSave}
            sx={{
              backgroundColor:
                theme.palette.mode === "dark"
                  ? colors.greenAccent || theme.palette.success.main
                  : colors.blueAccent || theme.palette.primary.main,
              color: "black",
              "&:hover": {
                backgroundColor:
                  theme.palette.mode === "dark"
                    ? colors.greenAccent || theme.palette.success.main
                    : colors.blueAccent || theme.palette.primary.main,
              },
            }}
          >
            Update Product
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setOpenSnackbar(false)}
          severity={snackbarSeverity}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default EditProductDialog;
